import React from 'react';

interface BatchRegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartBatch: () => void;
  csvData: any;
  userCredits: number | null;
  isOnline: boolean;
  titleLength: number;
  onTitleLengthChange: (value: number) => void;
  descriptionLength: number;
  onDescriptionLengthChange: (value: number) => void;
  keywordsCount: number;
  onKeywordsCountChange: (value: number) => void;
  customInstructions: string;
  onCustomInstructionsChange: (value: string) => void;
}

const BatchRegenerateModal: React.FC<BatchRegenerateModalProps> = ({
  isOpen,
  onClose,
  onStartBatch,
  csvData,
  userCredits,
  isOnline,
  titleLength,
  onTitleLengthChange,
  descriptionLength,
  onDescriptionLengthChange,
  keywordsCount,
  onKeywordsCountChange,
  customInstructions,
  onCustomInstructionsChange
}) => {
  if (!isOpen || !csvData) return null;

  const totalCost = csvData.rows.length;
  const hasEnoughCredits = userCredits !== null && userCredits >= totalCost;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Batch Regenerate All Metadata</h3>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 mb-3">
            <strong>This will regenerate metadata for all {csvData.rows.length} files using AI.</strong>
          </p>

          {userCredits !== null ? (
            hasEnoughCredits ? (
              <p className="text-sm text-green-700">
                💎 <strong>Cost: {totalCost} credits | Your current balance: {userCredits} credits</strong>
              </p>
            ) : (
              <div className="text-sm text-red-700">
                <p className="font-semibold mb-2">⚠️ INSUFFICIENT CREDITS</p>
                <p>💎 <strong>Cost: {totalCost} credits | Your current balance: {userCredits} credits</strong></p>
                <p className="mt-2">Please buy more credits to continue.</p>
              </div>
            )
          ) : (
            <p className="text-sm text-gray-600">
              💎 <strong>Cost: {totalCost} credits</strong>
            </p>
          )}
        </div>

        {/* AI Settings Section */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-800 mb-3">AI Generation Settings</h4>
          <div className="space-y-3">
            {/* Title Length */}
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0">
                Title: {titleLength} chars
              </label>
              <input
                type="range"
                min="30"
                max="150"
                value={titleLength}
                onChange={(e) => onTitleLengthChange(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Description Length */}
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0">
                Desc: {descriptionLength} chars
              </label>
              <input
                type="range"
                min="50"
                max="300"
                value={descriptionLength}
                onChange={(e) => onDescriptionLengthChange(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Keywords Count */}
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0">
                Keywords: {keywordsCount}
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={keywordsCount}
                onChange={(e) => onKeywordsCountChange(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Custom Instructions */}
            <div className="pt-2 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Instructions (Optional)
              </label>
              <textarea
                value={customInstructions}
                onChange={(e) => onCustomInstructionsChange(e.target.value)}
                placeholder="e.g., Focus on nature photography, use descriptive language..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {customInstructions.length} chars
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Important:</strong> This process cannot be cancelled once started.
            Credits will be charged even if you close the window.
          </p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          {userCredits !== null && !hasEnoughCredits ? (
            <button
              onClick={() => {
                onClose();
                window.open('/pricing', '_blank');
              }}
              className="flex-1 px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Buy More Credits
            </button>
          ) : (
            <button
              onClick={onStartBatch}
              className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Regeneration
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchRegenerateModal;

